import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isFeedbackConfigured, submitFeedback } from "../game/feedback";

const MAX_FILE_BYTES = 5 * 1024 * 1024;     // 5 MB per file
const MAX_TOTAL_BYTES = 12 * 1024 * 1024;   // 12 MB across all attachments

const CATEGORIES = [
  { value: "bug", label: "🐞 Bug" },
  { value: "idea", label: "💡 Idea / Feature" },
  { value: "rules", label: "📘 Rules clarification" },
  { value: "ux", label: "🎨 UX / Visual" },
  { value: "other", label: "💬 Other" },
];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  if (!isFeedbackConfigured()) return null;
  return (
    <>
      <button
        className="feedback-fab"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        title="Send feedback to the team"
      >
        <span>💬</span>
        <span>Feedback</span>
      </button>
      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  // Optional contact info — opt-in, only used for follow-up. We pre-fill from
  // localStorage if the user previously volunteered these values (one fewer
  // step next time) but never collect silently.
  const [name, setName] = useState(() => localStorage.getItem("lexikon.feedback.name") ?? "");
  const [email, setEmail] = useState(() => localStorage.getItem("lexikon.feedback.email") ?? "");
  const [contactOpen, setContactOpen] = useState(() =>
    Boolean(localStorage.getItem("lexikon.feedback.email") || localStorage.getItem("lexikon.feedback.name")),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Esc to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Paste images directly into the modal
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      const fresh: File[] = [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            const renamed = new File(
              [file],
              `pasted-${new Date().toISOString().replace(/[:.]/g, "-")}.${file.type.split("/")[1] || "png"}`,
              { type: file.type },
            );
            fresh.push(renamed);
          }
        }
      }
      if (fresh.length) {
        e.preventDefault();
        addFiles(fresh);
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  function addFiles(newFiles: File[]) {
    setError(null);
    const accepted: File[] = [];
    let runningTotal = files.reduce((s, f) => s + f.size, 0);
    for (const f of newFiles) {
      if (f.size > MAX_FILE_BYTES) {
        setError(`${f.name} is too large (max 5 MB per file).`);
        continue;
      }
      if (runningTotal + f.size > MAX_TOTAL_BYTES) {
        setError(`Total attachments would exceed ${formatBytes(MAX_TOTAL_BYTES)}.`);
        break;
      }
      accepted.push(f);
      runningTotal += f.size;
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = ""; // allow re-picking same file
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files));
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    if (!message.trim()) {
      setError("Please write a message.");
      return;
    }
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("That email looks malformed — leave it blank or fix it.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitFeedback({
        category,
        message: message.trim(),
        files,
        name: name.trim(),
        email: email.trim(),
      });
      // Remember contact info ONLY if user provided it, so next time is one-click.
      if (name.trim()) localStorage.setItem("lexikon.feedback.name", name.trim());
      if (email.trim()) localStorage.setItem("lexikon.feedback.email", email.trim());
      setSent(true);
      setTimeout(onClose, 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="zoom-backdrop" onClick={() => !busy && onClose()} role="dialog" aria-modal="true">
      <div className="zoom-card feedback-card" onClick={(e) => e.stopPropagation()}>
        <button className="zoom-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        {sent ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <h3 style={{ margin: "8px 0" }}>Thanks!</h3>
            <p style={{ color: "var(--ink-muted)", margin: 0 }}>Feedback sent — the team will see it on the sheet.</p>
          </div>
        ) : (
          <>
            <h3 style={{ margin: 0 }}>Send feedback</h3>
            <p style={{ color: "var(--ink-muted)", margin: 0, fontSize: 13 }}>
              Current page, your display name, and browser info are auto-included.
            </p>

            <label className="field">
              Category
              <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={busy}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>

            <label className="field">
              Message
              <textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened, what you expected, steps to reproduce…"
                disabled={busy}
                autoFocus
              />
            </label>

            <details
              className="feedback-contact"
              open={contactOpen}
              onToggle={(e) => setContactOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary>
                <span>Add your name / email <span className="optional">— optional, only if you'd like a reply</span></span>
              </summary>
              <p className="feedback-contact__note">
                We won't add you to any list or share these. Stored only on the team's playtest sheet alongside this message.
              </p>
              <label className="field">
                Name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  maxLength={64}
                  disabled={busy}
                  autoComplete="name"
                />
              </label>
              <label className="field">
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ada@example.com — leave blank for no reply"
                  maxLength={128}
                  disabled={busy}
                  autoComplete="email"
                />
              </label>
            </details>

            <div
              className="feedback-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <strong>📎 Attach screenshots / files</strong>
              <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                Click, drop here, or paste from clipboard (Ctrl+V) · max 5 MB each, 12 MB total
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.txt,.log"
                onChange={onFilePick}
                style={{ display: "none" }}
                disabled={busy}
              />
            </div>

            {files.length > 0 && (
              <ul className="feedback-files">
                {files.map((f, i) => (
                  <li key={i}>
                    {f.type.startsWith("image/") ? (
                      <img src={URL.createObjectURL(f)} alt={f.name} />
                    ) : (
                      <span className="feedback-files__icon">📄</span>
                    )}
                    <span className="feedback-files__name">{f.name}</span>
                    <span className="feedback-files__size">{formatBytes(f.size)}</span>
                    <button className="btn ghost" onClick={() => removeFile(i)} disabled={busy} style={{ padding: "2px 6px", fontSize: 11 }}>
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {error && (
              <div style={{ background: "#fde2e1", color: "#9b2520", padding: 8, borderRadius: 6, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn primary" onClick={handleSubmit} disabled={busy || !message.trim()}>
                {busy ? "Sending…" : "Send feedback"}
              </button>
              <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
